import { useState } from "react";

import {
  LABEEB_CATALOG,
  LABEEB_ENVIRONMENT_SETTINGS,
  labeebDomainsOf,
  labeebPluginsOf,
  type LabeebCatalog,
  type LabeebEnvironmentSettings,
} from "../../api/labeeb";
import { type FleetMetricEnvelope, metricPath } from "../../api/mcp";
import { RecentChanges } from "../../components/audit/RecentChanges";
import { Gate } from "../../components/authority/Gate";
import { ResourceView, statOf } from "../../components/states";
import { Card, Page, Stat, StatRow } from "../../components/ui";
import { ConfirmDialog } from "../../components/write/ConfirmDialog";
import { WriteResult } from "../../components/write/EffectTiming";
import { WriteFailure } from "../../components/write/WriteFailure";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { useWrite } from "../../hooks/useWrite";
import { ProductSection } from "../../products/PerProduct";
import { Settings } from "../governance/Settings";
import { AiAppsPanel } from "./AiApps";
import { PausedToolsPanel } from "./PausedTools";

/**
 * Settings — the design's Policy tab, one collapsible block per product.
 *
 * One page because it is one question: "what is this assistant configured to
 * do?" An operator should not have to know which product owns a knob to find
 * it. LABELLED by product because the answer to "did my change take effect?"
 * depends entirely on which one it was — an MCP message template needs an
 * explicit apply, an allowed AI app is live in ~60s, and a Labeeb domain gate
 * applies from each business's next turn.
 *
 * MCP's block holds its settings and, folded in beside them, the AI-app
 * allowlist — which is a setting in the same sense: which clients may connect —
 * and the paused tools, which switch tools off for every merchant.
 * `/policy/apps` redirects here, so a link already pasted into a thread still
 * lands on this page.
 *
 * LABEEB'S HALF WAS REBUILT, not ported. Its old block held runtime flags and
 * a live-config editor; the new admin API has neither route
 * (`GET/PATCH /admin/flags` and `PATCH/DELETE /admin/config/{key}` are gone,
 * and `GET /admin/config` now returns deployment facts rather than a
 * key/value store). What replaces them is the one genuinely fleet-wide Labeeb
 * surface: the domains and plugins disabled for EVERY business.
 */
export function Flags({ config }: { config: AppConfig }) {
  // How much has been changed through this console today. Read-only: there is
  // no list on this page for a count to filter.
  const adminActions = useResource<FleetMetricEnvelope<"admin_actions_today">>(
    config,
    metricPath("admin_actions_today"),
  );
  const env = useResource<LabeebEnvironmentSettings>(config, LABEEB_ENVIRONMENT_SETTINGS);

  // What the environment is holding off, fleet-wide. A number worth a stat
  // because it is the one setting on this page that affects every merchant at
  // once — the rest are per-tier or per-app.
  const envOffOf = (d: LabeebEnvironmentSettings) =>
    (d?.settings?.disabled_domains?.length ?? 0) + (d?.settings?.disabled_plugins?.length ?? 0);
  const envOff = env.state.kind === "ready" ? envOffOf(env.state.data) : null;
  const actionsFigure = statOf(adminActions.state, (e) => e.value);
  const envFigure = statOf(env.state, envOffOf);

  return (
    <Page
      title="Settings"
      subtitle="What the assistant is configured to do, in each product that configures it."
    >
      <div className="flex flex-col gap-6">
        <StatRow>
          <Stat
            value={actionsFigure.value}
            label="Admin actions today"
            hint={actionsFigure.hint ?? "every write through this console, both products"}
          />
          <Stat
            value={envFigure.value}
            label="Disabled fleet-wide"
            tone={envOff ? "amber" : "neutral"}
            hint={envFigure.hint ?? "Labeeb domains and plugins off for every business"}
          />
        </StatRow>

        <ProductSection
          id="mcp"
          note="Refusal messages, the response shape, which AI apps may connect, and which tools are paused"
        >
          <div className="flex flex-col gap-4">
            <Settings config={config} embedded />
            <Card title="AI apps" note="Which AI apps merchants may connect to MCP">
              <AiAppsPanel config={config} />
            </Card>
            {/* Beside AI apps, as the doc places it: a fleet-wide MCP switch,
                not a tab of its own. Its own Card (the header line needs the
                data), anchored at #paused-tools for links pasted mid-incident. */}
            <PausedToolsPanel config={config} />
          </div>
        </ProductSection>

        <ProductSection id="labeeb" note="Domains and plugins disabled for every business">
          <EnvironmentSettings config={config} />
        </ProductSection>
      </div>
    </Page>
  );
}

/**
 * The environment's gates: domains and plugins off for every business.
 *
 * ONE WRITE turns a capability off for the whole fleet. The options are the
 * catalog's domains and plugins — a name the catalog does not list is not a
 * legal value — and confirming it takes the L3 literal `every business`
 * plus a reason, because the blast radius is every merchant's next turn.
 *
 * Checked means OFF. The document stores the disabled lists; an empty list
 * means nothing is held off.
 */
function EnvironmentSettings({ config }: { config: AppConfig }) {
  const env = useResource<LabeebEnvironmentSettings>(config, LABEEB_ENVIRONMENT_SETTINGS);
  const catalog = useResource<LabeebCatalog>(config, LABEEB_CATALOG);
  const write = useWrite(config);
  const [confirming, setConfirming] = useState(false);
  const [pickedDomains, setPickedDomains] = useState<string[] | null>(null);
  const [pickedPlugins, setPickedPlugins] = useState<string[] | null>(null);
  const s = env.state;
  const busy = write.state.kind === "submitting";

  const storedDomains = s.kind === "ready" ? (s.data?.settings?.disabled_domains ?? []) : [];
  const storedPlugins = s.kind === "ready" ? (s.data?.settings?.disabled_plugins ?? []) : [];
  const domains = pickedDomains ?? storedDomains;
  const plugins = pickedPlugins ?? storedPlugins;
  const version = s.kind === "ready" ? s.data?.version : undefined;
  const catalogDomains = catalog.state.kind === "ready" ? labeebDomainsOf(catalog.state.data) : [];
  const catalogPlugins = catalog.state.kind === "ready" ? labeebPluginsOf(catalog.state.data) : [];

  const same = (a: string[], b: string[]) =>
    a.length === b.length && a.every((x) => b.includes(x));
  const dirty = !same(domains, storedDomains) || !same(plugins, storedPlugins);

  function toggle(current: string[], set: (next: string[]) => void, name: string, on: boolean) {
    set(on ? [...current, name] : current.filter((x) => x !== name));
  }

  function submit(reason: string) {
    if (version === undefined) return;
    void write
      .submit(LABEEB_ENVIRONMENT_SETTINGS, {
        method: "PATCH",
        body: JSON.stringify({
          expected_version: version,
          reason,
          settings: {
            disabled_domains: catalogDomains.filter((d) => domains.includes(d)),
            disabled_plugins: catalogPlugins.filter((p) => plugins.includes(p)),
          },
        }),
      })
      .then((r) => {
        if (!r) return;
        setPickedDomains(null);
        setPickedPlugins(null);
        env.reload();
      });
    setConfirming(false);
  }

  return (
    <>
      <ResourceView state={s} what="the environment's settings" onRetry={env.reload} />
      {s.kind === "ready" && catalog.state.kind !== "ready" && (
        <ResourceView state={catalog.state} what="the catalog" onRetry={catalog.reload} />
      )}

      {s.kind === "ready" && catalog.state.kind === "ready" && (
        <div className="flex flex-col gap-4">
          {domains.length === 0 && plugins.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Nothing is disabled fleet-wide. Every domain and plugin is available to any
              business whose own settings allow it.
            </p>
          )}

          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Domains off for every business
            </legend>
            {catalogDomains.map((d) => (
              <label key={d} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={domains.includes(d)}
                  onChange={(e) => toggle(domains, setPickedDomains, d, e.target.checked)}
                />
                <span className="ref">{d}</span>
              </label>
            ))}
          </fieldset>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Plugins off for every business
            </legend>
            {catalogPlugins.map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={plugins.includes(p)}
                  onChange={(e) => toggle(plugins, setPickedPlugins, p, e.target.checked)}
                />
                <span className="ref">{p}</span>
              </label>
            ))}
          </fieldset>

          <p className="text-xs/5 text-slate-500 dark:text-slate-400">
            A checked name is off for every business. One that a business enables anyway still
            cannot use it — the environment wins, and its merchant page says so per domain.
            Changes apply from each business&rsquo;s next turn.
            {version !== undefined && (
              <>
                {" "}
                Document version <span className="ref">{version}</span>.
              </>
            )}
          </p>

          <Gate need="settings:write">
            <Button disabled={busy || !dirty || version === undefined} onClick={() => setConfirming(true)}>
              Save fleet-wide gates…
            </Button>
          </Gate>
        </div>
      )}

      {write.state.kind === "done" && (
        <div className="mt-3">
          <WriteResult title="Fleet gates saved" onDismiss={write.reset}>
            <p className="text-sm">Each business picks this up on its next turn.</p>
          </WriteResult>
        </div>
      )}
      <div className="mt-3">
        <WriteFailure
          state={write.state}
          onDismiss={write.reset}
          onReread={() => {
            write.reset();
            setPickedDomains(null);
            setPickedPlugins(null);
            env.reload();
          }}
        />
      </div>

      <ConfirmDialog
        open={confirming}
        title="Change what is off for every business"
        confirmLabel="Save"
        destructive
        requireReason
        typeToConfirm="every business"
        reasonHint="e.g. DA-1400, inventory answers are wrong"
        busy={busy}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
        effect="Applies from each business's next turn. A domain off here stays off whatever a business enables."
      >
        <p>
          This turns capabilities off or on for every business at once, not for one merchant.
        </p>
      </ConfirmDialog>

      <div className="mt-4">
        <RecentChanges
          config={config}
          product="labeeb"
          domain="settings"
          target="environment/settings"
        />
      </div>
    </>
  );
}
